<template>
	<fieldset class="get-started__locations">
		<aside
			v-show="google_api_key.length"
			:class="{ 
				'is-loaded': states.isLoaded,
				'is-loading': states.isLoading,
			}"			
			class="get-started__locations__map-container"
		>
			<div class="get-started__locations__map"></div>
		</aside>

		<label
			v-for="location in locations"
			:key="location.id"
			:for="`location-${location.id}`"
			class="get-started__location"
		>
			<input
				@input="handleSelectLocation( location.id )"
				:id="`location-${location.id}`"
				:checked="location.isSelected"
				:value="location.name"
				class="get-started__location__input input-choice input-choice--radio"
				name="user_location"
				type="radio"
			/>
			<span class="get-started__location__meta input-choice-value input-choice-value--radio">
				<p class="location__name flush-ends"><strong>{{ location.name }}</strong></p>
				<p
					v-html="location.address"
					class="location__address"
				></p>
			</span>
		</label>
	</fieldset>
</template>


<style lang="scss">
	.get-started__locations {

		&__map {
			position: relative;
			width: 100%;
			height: 16.25rem;
			margin-bottom: 1rem;

			&.is-loading:before,
			&.is-loading:after {
				content: '';
				position: absolute;
				top: 0;
				left: 0;
				display: block;
			}

			&.is-loading:before {
				top: 50%;
				left: 50%;
				width: 2rem;
				aspect-ratio: 1;
				border: 0.3125rem solid;
				border-top-color: transparent !important;
				border-radius: 50%;
				z-index: 5000;
				transform: translate( -50%, -50% );
				animation: isLoading 3s infinite linear; /* defined in main.css */
			}

			&.is-loading:after {
				width: 100%;
				height: 100%;
				background-color: #ebebeb;
				z-index: 1000;
			}
		}
	}
</style>


<script>
	export default {
		name: 'Map',

		props: {
			locations: {
				type: Array,
				default() {
					return []
				},
			},
			google_api_key: {
				type: String,
				default: '',
			},
		},



		data() {
			return {
				map: null,
				markers: [],
				states: {
					isLoading: false,
					isLoaded: false
				},
			}
		},



		watch: {

			// double-check that active location's marker has been updated
			locations: {
				deep: true,
				immediate: true,
				handler( locations ) {
					const activeLocation = locations.find( location => location.isSelected )

					if( activeLocation ) {
						this.setActiveMapMarker( activeLocation.id )
					}
				}
			}
		},



		methods: {

			/**
			 * Updates map loading state
			 * 
			 * @param	{bool}	isLoading 	If true, adds loading animation to map
			 * @param	{bool}	isLoaded 	If true, removes loading animation from map
			 */
			updateMapLoadState( isLoading = false, isLoaded = false ) {
				this.states.isLoading = isLoading
				this.states.isLoaded = isLoaded
			},


			/**
			 * Loads Google Map JS and attaches callback for map's onload event
			 * 
			 */
			loadGoogleMap() {

				// reset states
				this.updateMapLoadState()

				if( !this.google_api_key ) {
					return
				}

				// adds loading animation to element
				this.updateMapLoadState( true )

				// function to run once Google Maps has initialized
				window.fxOnlineStoreGoogleMapInit = () => this.renderGoogleMap()

				const scriptUrl = new URL( 'https://maps.googleapis.com/maps/api/js' )

				scriptUrl.searchParams.set( 'key', this.google_api_key )
				scriptUrl.searchParams.set( 'callback', 'fxOnlineStoreGoogleMapInit' )

				const googleScript = document.createElement('script')

				googleScript.src = scriptUrl.toString()
				googleScript.defer = true

				document.head.appendChild( googleScript )
			},	


			/**
			 * Creates interactive Google Map and adds locations as pins
			 * 
			 */
			renderGoogleMap() {
				const self = this,
					gmaps = window.google.maps // circumvents eslint errors

				try {
					const el = self.$el.querySelector('.get-started__locations__map')
					
					if( null === el ) {
						throw Error( 'No map element exists' )
					}

					// styling grabbed from WPCM (/our-locations)
					self.map = new gmaps.Map( el, {
						zoom: 10,
						styles: [{"featureType":"water","stylers":[{"visibility":"on"},{"color":"#b5cbe4"}]},{"featureType":"landscape","stylers":[{"color":"#efefef"}]},{"featureType":"road.highway","elementType":"geometry","stylers":[{"color":"#83a5b0"}]},{"featureType":"road.arterial","elementType":"geometry","stylers":[{"color":"#bdcdd3"}]},{"featureType":"road.local","elementType":"geometry","stylers":[{"color":"#ffffff"}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#e3eed3"}]},{"featureType":"administrative","stylers":[{"visibility":"on"},{"lightness":33}]},{"featureType":"road"},{"featureType":"poi.park","elementType":"labels","stylers":[{"visibility":"on"},{"lightness":20}]},{},{"featureType":"road","stylers":[{"lightness":20}]}]
					})

					// todo — remove pre-launch
					window.fxDebugMap = self.map

					const bounds = new gmaps.LatLngBounds(),
						iconBase = {
							anchor: new gmaps.Point( 10, 26 ),
							fillOpacity: 1,
							path: 'M10,0C4.48,0,0,4.05,0,9,0,15.3,10,26,10,26S20,15.06,20,9C20,4.05,15.52,0,10,0Zm3,11.69a4.6,4.6,0,0,1-6-.08A3.6,3.6,0,0,1,7,6.24a4.59,4.59,0,0,1,6,0A3.6,3.6,0,0,1,13,11.69Z',
							strokeColor: '#fff',
							strokeWidth: 1,
						}

					self.locations.forEach( location => {
						const icon = iconBase

						// icon yellow if selected; otherwise gray
						icon.fillColor = location.isSelected ? '#e7b541' : '#353535'

						const position = new gmaps.LatLng( location.lat, location.lng ),
							marker = new gmaps.Marker({
								icon,
								position,
								map: self.map,
								title: location.name,
								fxLocation: {
									...location
								}
							})

						bounds.extend( marker.position )

						gmaps.event.addListener( marker, 'click', () => {
							self.handleSelectLocation( location.id )
						})

						self.markers.push( marker )
					})

					self.map.setCenter( bounds.getCenter() )
					self.map.fitBounds( bounds )
					self.map.setZoom( 10 ) // todo — confirm this works after moving to live (probably won't work if API key is invalid)

				} catch( err ) {
					console.warn( `Encountered error rendering map: "${err}"` )
				}

				self.updateMapLoadState( false, true )
			},			


			/**
			 * Update Google Map markers to highlight currently active location
			 * 
			 * @param	{number}	locationId 	Post ID for active location
			 */
			setActiveMapMarker( locationId ) {
				this.markers.forEach( marker => {
					marker.icon.fillColor = 
						( locationId === marker.fxLocation.id ) 
						? '#e7b541' 
						: '#353535'

					marker.setIcon( marker.icon )
				})
			},


			/**
			 * Update Google Map and send active location to parent component
			 * 
			 * @param	{number}	locationId 	Post ID for active location
			 */
			handleSelectLocation( locationId ) {
				this.setActiveMapMarker( locationId )

				// event propagates to Store.vue, which will then update radio button states
				this.$emit( 'user_selected_location', locationId )
			},
		},


		mounted() {
			if( this.google_api_key ) {
				this.loadGoogleMap()
			}
		}
	}
</script>
